import { useCallback, useEffect, useMemo, useState } from "react";

import {
  VehicleReferenceItem,
  VehicleReferenceList,
  getBulletinTypesPublic,
  getCrimeTypes,
  getPoliceServices,
  getVehicleMakesPublic,
  getVehicleModelsPublic,
} from "@rubi/api-generated";
import { useTranslation } from "react-i18next";

import {
  VEHICLE_OTHER,
  VEHICLE_UNKNOWN,
} from "@/components/alerts/add-v2/model/entityRules";
import { IAssociatedCrime, IPoliceService } from "@/types";

/**
 * The option lists the setup, incident and entity fields need: local state, a `loading` flag, and the
 * request fired **when the field mounts** — not when its menu opens.
 *
 * Loading on open is what this used to do (README decision 5, now amended). It was wrong for the same
 * reason twice: half of these lists are not just options, they are the only thing that can **name the
 * value the form already holds**. A bulletin type is stored as `auto_theft`, a crime as its key, and
 * an operational area is not even offered until the police-service list is in — so an edit-mode form
 * read back keys, and one field did not render at all, until the officer opened each menu in turn.
 *
 * Sections fetch their own option lists rather than taking them as props — README decision 14. The
 * choice of this over the React Query hooks in `api/loadAlerts.ts` is decision 20, which also records
 * what it costs.
 *
 * The two public endpoints take the language as `Accept-Language`, read from i18next rather than from
 * the `language=` cookie (which is what `api/loadAlerts.ts` does): `AppBar` already syncs the cookie
 * into `i18n`, so the two agree, and this avoids a fourth hand-rolled cookie parse.
 */

interface AsyncOptions<T> {
  options: T[];
  loading: boolean;
}

/** `suggested` is the pinned head of a vehicle list; `all` is the rest, in one flat array. */
export interface VehicleOption extends VehicleReferenceItem {
  group: "suggested" | "all";
}

/**
 * Flattens the endpoint's `{popular, all}` into one grouped array.
 *
 * `all` deliberately contains everything the endpoint returned, popular included, so each half of
 * the response means what its name says. Removing the overlap is the caller's job — here — because
 * an Autocomplete showing Honda twice is a bug.
 */
export const toVehicleOptions = (
  list: VehicleReferenceList | undefined,
): VehicleOption[] => {
  const popular = list?.popular ?? [];
  const pinned = new Set(popular.map((item) => item.key));

  return [
    ...popular.map((item) => ({ ...item, group: "suggested" as const })),
    ...(list?.all ?? [])
      .filter((item) => !pinned.has(item.key))
      .map((item) => ({ ...item, group: "all" as const })),
  ];
};

/** Nothing invalidates the police-service list, so its reload key is a constant. */
const LOAD_ONCE = "once";

/**
 * Loads once per `reloadKey`, when the field mounts and again whenever the key changes — the key is
 * the language for the translated lists and `language:make` for vehicle models, so a language switch
 * or a new make reloads and nothing else does.
 *
 * A loaded list is stored **with the key it was loaded for**, and only served while that key is still
 * the current one. Otherwise the model field shows the previous make's models for as long as the new
 * request takes: the key changes the moment the make does, but the options do not. The cleanup does
 * the other half — switching Honda → Toyota faster than Honda's request resolves cannot write
 * Honda's models under Toyota.
 *
 * **One retry, then the field stays empty.** With nothing loading on open there is no menu left to
 * retry on, and the failure this actually sees is one dropped request from a cruiser with patchy
 * coverage — the same one README decision 37 has autosave retry, handled the same way.
 */
const useLoadedList = <T>(
  reloadKey: string,
  load: () => Promise<T[]>,
): AsyncOptions<T> => {
  const [loaded, setLoaded] = useState<{ key: string; options: T[] } | null>(
    null,
  );
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  useEffect(() => {
    let current = true;

    setLoadingKey(reloadKey);
    load()
      .catch(() => load())
      .then((options) => {
        if (current) setLoaded({ key: reloadKey, options });
      })
      .catch(() => {
        // Both attempts failed. The field is empty, which `loading` has already stopped claiming.
      })
      .finally(() => {
        if (current) setLoadingKey(null);
      });

    return () => {
      current = false;
    };
  }, [reloadKey, load]);

  return {
    options: loaded?.key === reloadKey ? loaded.options : [],
    loading: loadingKey === reloadKey,
  };
};

export const usePoliceServiceOptions = (): AsyncOptions<IPoliceService> => {
  const load = useCallback(
    () =>
      getPoliceServices().then(
        // Same cast `AlertViewProvider` makes: the generated `GroupResource` is wider than portal's
        // hand-written group types, and `IPoliceService` is a `Pick` of three of its fields.
        (res) => (res.data ?? []) as unknown as IPoliceService[],
      ),
    [],
  );

  return useLoadedList(LOAD_ONCE, load);
};

interface BulletinTypeOptions extends AsyncOptions<string> {
  /** `bulletinType` stores a `reference_name`; this resolves it to the translated name. */
  getOptionLabel: (referenceName: string) => string;
}

/**
 * Bulletin types. `bulletinType` stores a `reference_name`, so this list is also the only thing that
 * can turn `auto_theft` into "Auto Theft" — see the file's own comment for why that stopped being
 * something an officer had to open a menu to find out.
 */
export const useBulletinTypeOptions = (): BulletinTypeOptions => {
  const { i18n } = useTranslation();
  const language = i18n.language;

  const load = useCallback(
    () =>
      getBulletinTypesPublic({
        headers: { "Accept-Language": language },
      }).then((res) => res.data ?? []),
    [language],
  );

  const { options, loading } = useLoadedList(language, load);

  const referenceNames = useMemo(
    () => options.map((type) => type.reference_name),
    [options],
  );

  const getOptionLabel = useCallback(
    (referenceName: string) =>
      options.find((type) => type.reference_name === referenceName)?.name ??
      // Only while the request is in flight, or after both attempts failed. Showing the key beats
      // showing an empty field, which reads as "no type chosen".
      referenceName,
    [options],
  );

  return { options: referenceNames, loading, getOptionLabel };
};

interface CrimeTypeOptions extends AsyncOptions<IAssociatedCrime> {
  /**
   * A stored crime resolved to its translated name.
   *
   * The wire carries the key alone (`adapters.ts`, "What the wire cannot carry"), so a crime restored
   * from a bulletin has no `name` of its own and this list is the only thing that has one. A crime the
   * officer picked in this session does carry it, which is the fallback — and the key itself is the
   * last resort, for the window before the list lands.
   */
  getOptionLabel: (crime: IAssociatedCrime) => string;
}

export const useCrimeTypeOptions = (): CrimeTypeOptions => {
  const { i18n } = useTranslation();
  const language = i18n.language;

  const load = useCallback(
    () =>
      getCrimeTypes({ headers: { "Accept-Language": language } }).then((res) =>
        // The endpoint groups crimes by category; the form stores them flat.
        (res.data ?? []).flatMap((group) =>
          group.crimes.map((crime) => ({
            crime: crime.key,
            group: group.group_key,
            name: crime.name,
          })),
        ),
      ),
    [language],
  );

  const { options, loading } = useLoadedList<IAssociatedCrime>(language, load);

  const getOptionLabel = useCallback(
    (crime: IAssociatedCrime) =>
      options.find((option) => option.crime === crime.crime)?.name ??
      crime.name ??
      crime.crime,
    [options],
  );

  return { options, loading, getOptionLabel };
};

/**
 * Vehicle makes, from NHTSA vPIC by way of our own endpoint.
 *
 * The backend merges the three vehicle types, normalises vPIC's block capitals, pins the curated
 * list and — the reason it is a backend call rather than a fetch straight to vPIC — serves the
 * curated list alone when vPIC is unreachable, so the field never breaks.
 */
export const useVehicleMakeOptions = (): AsyncOptions<VehicleOption> => {
  const { i18n } = useTranslation();
  const language = i18n.language;

  const load = useCallback(
    () =>
      getVehicleMakesPublic({ headers: { "Accept-Language": language } }).then(
        (res) => toVehicleOptions(res.data),
      ),
    [language],
  );

  return useLoadedList(language, load);
};

/**
 * Models for one make. The reload key is the make, so picking a different one loads its own list on
 * the next open rather than showing the previous make's models.
 *
 * A sentinel make (`unknown` / `other`) has no model list — `applyVehicleMake` has already forced
 * the model to `other`, and the field renders as free text — so nothing is requested for it.
 */
export const useVehicleModelOptions = (
  make: string,
): AsyncOptions<VehicleOption> => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const loadable =
    make !== "" && make !== VEHICLE_UNKNOWN && make !== VEHICLE_OTHER;

  const load = useCallback(
    () =>
      loadable
        ? getVehicleModelsPublic({
            path: { make },
            headers: { "Accept-Language": language },
          }).then((res) => toVehicleOptions(res.data))
        : Promise.resolve([]),
    [make, language, loadable],
  );

  return useLoadedList(`${language}:${make}`, load);
};

/**
 * The operational areas offered for the currently selected police service, or an empty list when it
 * defines none — in which case the field is not shown at all, matching the old flow.
 *
 * The fetched list is preferred over the value held in form state because in edit mode the police
 * service arrives as the bulletin's `issuing_police_service`, which carries `id` and `name` but no
 * `rubi_org`. The old flow does the same lookup for the same reason.
 *
 * This used to return nothing in edit mode until the police-service menu had been opened once, which
 * meant the field did not render at all and the officer could not see the area the bulletin holds.
 * Loading the list on mount is what fixed it; the fallback to the stored value's own `rubi_org` is
 * kept for the window before the list lands, and for a create flow that already has the full object.
 */
export const useOperationalAreas = (
  policeService: IPoliceService | null,
  policeServices: IPoliceService[],
): string[] => {
  const matched = policeServices.find(
    (service) => service.id === policeService?.id,
  );

  return (
    matched?.rubi_org?.operational_areas ??
    policeService?.rubi_org?.operational_areas ??
    []
  );
};
